// How wide the filter panel beside the drawing is, and how far a drag may take it. Here rather
// than inline in the page so the numbers have one home and the clamp can be tested without
// simulating a pointer — the same split `lib/assistant-width.ts` makes for the drawer.

/** Opening width in px, before anybody drags it. */
export const DEFAULT_SIDE_WIDTH = 300;

/**
 * Narrower than this and the panel stops being usable: a company name is a legal entity with a
 * legal form on the end of it, the counts sit on the right of the same row, and below about this
 * every row is an ellipsis with a number after it.
 */
export const MIN_SIDE_WIDTH = 220;

/**
 * The panel never takes more than half the window, so the drawing it filters is always still on
 * screen. A filter that can cover what it filters is a filter nobody can use.
 */
export function maxSideWidth(viewportWidth: number): number {
  return Math.floor(viewportWidth / 2);
}

export function clampSideWidth(next: number, viewportWidth: number): number {
  return Math.min(maxSideWidth(viewportWidth), Math.max(MIN_SIDE_WIDTH, next));
}

/**
 * Shorter than this and a section is its own header and nothing else.
 *
 * The lists keep their own scroll, so a short one is still usable — but a pane dragged to zero
 * leaves no grip to drag it back with, which is the one state a resize must not be able to reach.
 */
export const MIN_PANE = 72;

/** A pane's weight, or null while it is still sized to its own content. */
export type Share = number | null;

/**
 * The flex weights after dragging the rule between pane [at] and the one below it.
 *
 * WEIGHTS, NOT PIXELS. The panes share whatever height the column has, so a drag that wrote pixels
 * would fix them there and the sidebar would stop answering to the window. Only the PAIR either
 * side of the rule changes, and their combined weight is preserved — so the panes a reader did not
 * touch keep the share they had.
 *
 * Null when either side of the pair would fall under [MIN_PANE]: the caller holds the last good
 * split rather than clamping to it, so a pointer dragged well past the end does not park the rule
 * at the floor and make the way back feel sticky.
 */
export function resizePanes(
  shares: readonly Share[],
  at: number,
  aPx: number,
  bPx: number,
): Share[] | null {
  if (aPx < MIN_PANE || bPx < MIN_PANE) return null;
  const pair = (shares[at] ?? 1) + (shares[at + 1] ?? 1);
  const span = aPx + bPx;
  return shares.map((weight, index) =>
    index === at ? (pair * aPx) / span : index === at + 1 ? (pair * bPx) / span : weight,
  );
}

/**
 * The weight a pane still at its content height is worth, beside the one above it.
 *
 * THE LAST PANE IS NOT A LIST. The relations are eight rows fixed by the predicate vocabulary, so
 * sized to its content it is exactly right and an equal quarter of the column would be wrong. It
 * stays that way until someone drags the rule above it, and joins the split at what it is already
 * worth — so the first drag moves the rule and nothing else jumps.
 */
export function shareOf(above: Share, abovePx: number, ownPx: number): number {
  return abovePx > 0 ? ((above ?? 1) * ownPx) / abovePx : 1;
}
